import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useCustomMutation } from '@refinedev/core';
import { ShieldCheck, ShieldOff, Loader2, Users, Check, X } from 'lucide-react';
import type { SegmentDefinition } from '@masters-club/validation';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { DataTable, type Column } from '@/components/tables/data-table';
import { UserPreviewCell } from '@/components/shared';
import { getFlagEmoji } from '@/resources/languages/types';
import { notify } from '@/lib/notify';

// ─── Типы ответа preview (бэкенд audience.service.AudiencePreviewUser) ──────

interface AudienceSubscription {
  id: number;
  status: string;
  endDate: string | null;
}

interface AudienceTag {
  id: number;
  name: string;
  color: string;
}

export interface AudienceTableUser {
  id: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  email: string | null;
  telegramId: number | null;
  photoUrl: string | null;
  phone: string | null;
  languagePreference: string | null;
  createdAt: string | null;
  accountStatus: 'active' | 'blocked' | 'deleted';
  subscriptions: AudienceSubscription[];
  tags: AudienceTag[];
  /** Только для трансляций: есть ли активный доступ к этой трансляции. */
  hasAccess?: boolean;
  /**
   * Соответствует ли юзер сегменту, переданному в `body.definition`
   * (заполняется только если backend получил matchSegmentDefinition).
   */
  matchesSegment?: boolean;
}

interface PreviewResponse {
  total: number;
  users: AudienceTableUser[];
}

interface GrantResponse {
  granted: number;
  skipped: number;
}

interface RevokeResponse {
  revoked: number;
}

function isPreviewResponse(v: unknown): v is PreviewResponse {
  return (
    typeof v === 'object' &&
    v !== null &&
    'total' in v &&
    'users' in v &&
    Array.isArray((v as PreviewResponse).users)
  );
}

function isGrantResponse(v: unknown): v is GrantResponse {
  return (
    typeof v === 'object' && v !== null && 'granted' in v &&
    typeof (v as GrantResponse).granted === 'number'
  );
}

function isRevokeResponse(v: unknown): v is RevokeResponse {
  return (
    typeof v === 'object' && v !== null && 'revoked' in v &&
    typeof (v as RevokeResponse).revoked === 'number'
  );
}

const PREVIEW_LIMIT = 200;
const PAGE_SIZE = 20;

// ─── Бейджи (идентичны разделу «Пользователи») ──────────────────────────────

function subscriptionBadge(status: string | undefined) {
  switch (status) {
    case 'active':
    case 'ACTIVE':
      return (
        <Badge className="bg-green-500/10 text-green-500 hover:bg-green-500/20">Активна</Badge>
      );
    case 'expired':
    case 'EXPIRED':
      return <Badge variant="secondary">Истекла</Badge>;
    case 'cancelled':
    case 'CANCELLED':
      return <Badge variant="destructive">Отменена</Badge>;
    default:
      return <Badge variant="outline">Нет подписки</Badge>;
  }
}

function accountStatusBadge(status: AudienceTableUser['accountStatus']) {
  if (status === 'deleted') {
    return <Badge variant="outline">Удалён</Badge>;
  }
  if (status === 'blocked') {
    return <Badge variant="destructive">Заблокирован</Badge>;
  }
  return (
    <Badge className="bg-green-500/10 text-green-600 hover:bg-green-500/20">Активен</Badge>
  );
}

function TagsBadges({ tags }: { tags: AudienceTag[] }) {
  if (tags.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  const visible = tags.slice(0, 3);
  const hidden = tags.length - visible.length;
  return (
    <div className="flex flex-wrap gap-1">
      {visible.map((tag) => (
        <span
          key={tag.id}
          className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium text-white"
          style={{ backgroundColor: tag.color }}
        >
          {tag.name}
        </span>
      ))}
      {hidden > 0 && (
        <Badge variant="outline" className="text-xs">
          +{hidden}
        </Badge>
      )}
    </div>
  );
}

function formatDate(dateString: string | null): string {
  if (!dateString) {
    return '—';
  }
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// ─── Компонент ──────────────────────────────────────────────────────────────

export interface AudienceUsersTableProps {
  /** URL preview-эндпойнта (data-provider custom): '/audience/preview'
   *  или '/admin/livestreams/:id/audience/preview'. */
  previewUrl: string;
  /** Текущий сегмент-фильтр. */
  definition: SegmentDefinition;
  /** Доп. тело запроса (например includeUserIds для рассылок). */
  extraBody?: Record<string, unknown>;
  disabled?: boolean;
  /** Показать колонку «Доступ» (только трансляции). */
  showAccessColumn?: boolean;
  /** URL grant/revoke для точечной выдачи доступа (только трансляции). */
  grantUrl?: string;
  revokeUrl?: string;
  /** Колбэк с общим числом аудитории (для внешнего счётчика, напр. рассылки). */
  onTotalChange?: (total: number) => void;
  /** Колбэк выбранных пользователей (для рассылок — includeUserIds). */
  onSelectedChange?: (userIds: number[]) => void;
  /**
   * Показать колонку с признаком соответствия сегменту
   * (для промокодов: «Промокод сработает / не сработает»).
   * Backend должен заполнить `matchesSegment` на каждой строке.
   */
  showMatchColumn?: boolean;
  /** Заголовок match-колонки. Default: «Сработает». */
  matchColumnHeader?: string;
  /** Подпись для matched=true. Default: «Да». */
  matchTrueLabel?: string;
  /** Подпись для matched=false. Default: «Нет». */
  matchFalseLabel?: string;
}

export function AudienceUsersTable({
  previewUrl,
  definition,
  extraBody,
  disabled = false,
  showAccessColumn = false,
  grantUrl,
  revokeUrl,
  onTotalChange,
  onSelectedChange,
  showMatchColumn = false,
  matchColumnHeader = 'Сработает',
  matchTrueLabel = 'Да',
  matchFalseLabel = 'Нет',
}: AudienceUsersTableProps) {
  const [users, setUsers] = useState<AudienceTableUser[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [selectedRows, setSelectedRows] = useState<AudienceTableUser[]>([]);
  // По умолчанию показываем ВСЕХ пользователей (можно выдать доступ кому
  // угодно напрямую). Переключатель сужает до условий сегмента.
  const [onlyFilter, setOnlyFilter] = useState(false);

  const requestIdRef = useRef(0);
  const { mutate: fetchPreview } = useCustomMutation<PreviewResponse>();
  const { mutate: grantAccess, mutation: grantM } = useCustomMutation<GrantResponse>();
  const { mutate: revokeAccess, mutation: revokeM } = useCustomMutation<RevokeResponse>();
  const isBusy = disabled || grantM.isPending || revokeM.isPending;

  const loadPreview = useCallback(() => {
    const reqId = ++requestIdRef.current;
    const isCurrent = () => reqId === requestIdRef.current;
    setIsLoading(true);
    setPage(1);

    fetchPreview(
      {
        url: previewUrl,
        method: 'post',
        values: {
          definition,
          limit: PREVIEW_LIMIT,
          search: search || undefined,
          allUsers: !onlyFilter,
          ...extraBody,
        },
      },
      {
        onSuccess: (r) => {
          if (!isCurrent()) {
            return;
          }
          if (isPreviewResponse(r.data)) {
            setUsers(r.data.users);
            setTotal(r.data.total);
          }
          setIsLoading(false);
        },
        onError: () => {
          if (!isCurrent()) {
            return;
          }
          setUsers([]);
          setTotal(0);
          setIsLoading(false);
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPreview, previewUrl, definition, search, extraBody, onlyFilter]);

  useEffect(() => {
    const t = setTimeout(loadPreview, 600);
    return () => clearTimeout(t);
  }, [loadPreview]);

  const paginated = useMemo(
    () => users.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [users, page],
  );

  const withAccessCount = useMemo(
    () => users.filter((u) => u.hasAccess).length,
    [users],
  );

  const matchedCount = useMemo(
    () => users.filter((u) => u.matchesSegment === true).length,
    [users],
  );

  const selectedIds = useMemo(() => selectedRows.map((r) => r.id), [selectedRows]);

  const onTotalRef = useRef(onTotalChange);
  onTotalRef.current = onTotalChange;
  useEffect(() => {
    onTotalRef.current?.(total);
  }, [total]);

  const onSelectedRef = useRef(onSelectedChange);
  onSelectedRef.current = onSelectedChange;
  useEffect(() => {
    onSelectedRef.current?.(selectedIds);
  }, [selectedIds]);

  const handleGrant = useCallback(() => {
    if (!grantUrl || selectedIds.length === 0) {
      return;
    }
    grantAccess(
      { url: grantUrl, method: 'post', values: { userIds: selectedIds } },
      {
        onSuccess: (r) => {
          if (isGrantResponse(r.data)) {
            notify.success(
              `Доступ выдан: ${r.data.granted}${r.data.skipped > 0 ? `, пропущено ${r.data.skipped}` : ''}`,
            );
          }
          loadPreview();
        },
        onError: () => notify.error('Не удалось выдать доступ'),
      },
    );
  }, [grantUrl, grantAccess, selectedIds, loadPreview]);

  const handleRevoke = useCallback(() => {
    if (!revokeUrl || selectedIds.length === 0) {
      return;
    }
    revokeAccess(
      { url: revokeUrl, method: 'post', values: { userIds: selectedIds } },
      {
        onSuccess: (r) => {
          if (isRevokeResponse(r.data)) {
            notify.success(`Доступ отозван: ${r.data.revoked}`);
          }
          loadPreview();
        },
        onError: () => notify.error('Не удалось отозвать доступ'),
      },
    );
  }, [revokeUrl, revokeAccess, selectedIds, loadPreview]);

  const columns: Column<AudienceTableUser>[] = useMemo(() => {
    const cols: Column<AudienceTableUser>[] = [
      { key: 'id', header: 'ID', width: '70px' },
      {
        key: 'firstName',
        header: 'Имя',
        render: (_, u) => <UserPreviewCell user={u} />,
      },
      {
        key: 'telegramId',
        header: 'Telegram ID',
        render: (value) =>
          value ? (
            <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{String(value)}</code>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        key: 'phone',
        header: 'Телефон',
        render: (value) => (value ? String(value) : '—'),
      },
      {
        key: 'languagePreference',
        header: 'Язык',
        width: '90px',
        render: (_, u) =>
          u.languagePreference ? (
            <span className="inline-flex items-center gap-1.5">
              <span>{getFlagEmoji(u.languagePreference)}</span>
              <span className="uppercase text-xs text-muted-foreground">
                {u.languagePreference}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        key: 'subscriptions',
        header: 'Подписка',
        render: (_, u) => subscriptionBadge(u.subscriptions[0]?.status),
      },
      {
        key: 'tags',
        header: 'Теги',
        render: (_, u) => <TagsBadges tags={u.tags} />,
      },
      {
        key: 'accountStatus',
        header: 'Статус',
        width: '120px',
        render: (_, u) => accountStatusBadge(u.accountStatus),
      },
      {
        key: 'createdAt',
        header: 'Регистрация',
        render: (value) => formatDate(typeof value === 'string' ? value : null),
      },
    ];

    if (showAccessColumn) {
      cols.push({
        key: 'hasAccess',
        header: 'Доступ',
        width: '130px',
        render: (_, u) =>
          u.hasAccess ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-950 dark:text-green-400">
              <ShieldCheck className="h-3 w-3" />
              Доступ выдан
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      });
    }

    if (showMatchColumn) {
      cols.push({
        key: 'matchesSegment',
        header: matchColumnHeader,
        width: '140px',
        render: (_, u) => {
          if (u.matchesSegment === true) {
            return (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-950 dark:text-green-400">
                <Check className="h-3 w-3" />
                {matchTrueLabel}
              </span>
            );
          }
          if (u.matchesSegment === false) {
            return (
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                <X className="h-3 w-3" />
                {matchFalseLabel}
              </span>
            );
          }
          return <span className="text-xs text-muted-foreground">—</span>;
        },
      });
    }

    return cols;
  }, [showAccessColumn, showMatchColumn, matchColumnHeader, matchTrueLabel, matchFalseLabel]);

  const hasAccessActions = Boolean(grantUrl || revokeUrl);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex h-9 shrink-0 items-center gap-2 rounded-md border px-3">
          <Users className="h-4 w-4 text-primary" />
          <span className="text-sm text-muted-foreground">В аудитории:</span>
          <span className="text-sm font-semibold tabular-nums">
            {total.toLocaleString('ru-RU')}
          </span>
          {isLoading && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          )}
        </div>

        <div className="inline-flex h-9 shrink-0 items-center gap-2 rounded-md border px-3">
          <Switch
            id="audience-only-filter"
            checked={onlyFilter}
            onCheckedChange={setOnlyFilter}
            disabled={isBusy}
          />
          <Label
            htmlFor="audience-only-filter"
            className="text-sm text-muted-foreground cursor-pointer"
          >
            Только по условиям
          </Label>
        </div>

        {showAccessColumn && withAccessCount > 0 && (
          <div className="inline-flex h-9 shrink-0 items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 dark:border-green-900 dark:bg-green-950/30">
            <ShieldCheck className="h-4 w-4 text-green-600 dark:text-green-400" />
            <span className="text-sm text-muted-foreground">Уже с доступом:</span>
            <span className="text-sm font-semibold tabular-nums text-green-700 dark:text-green-400">
              {withAccessCount}
            </span>
          </div>
        )}

        {showMatchColumn && (
          <div className="inline-flex h-9 shrink-0 items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 dark:border-green-900 dark:bg-green-950/30">
            <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
            <span className="text-sm text-muted-foreground">{matchColumnHeader}:</span>
            <span className="text-sm font-semibold tabular-nums text-green-700 dark:text-green-400">
              {matchedCount}
            </span>
            <span className="text-xs text-muted-foreground">из {users.length}</span>
          </div>
        )}

        <div className="flex-1" />

        {hasAccessActions && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleGrant}
                    disabled={isBusy || selectedRows.length === 0 || !grantUrl}
                    className="gap-1.5"
                  >
                    {grantM.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <ShieldCheck className="h-3.5 w-3.5" />
                    )}
                    Выдать доступ
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Выдать доступ выбранным на этой странице</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {hasAccessActions && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleRevoke}
            disabled={isBusy || selectedRows.length === 0 || !revokeUrl}
            className="gap-1.5 text-destructive hover:text-destructive"
          >
            {revokeM.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ShieldOff className="h-3.5 w-3.5" />
            )}
            Отозвать доступ
          </Button>
        )}
      </div>

      <DataTable<AudienceTableUser>
        columns={columns}
        data={paginated}
        total={Math.min(total, PREVIEW_LIMIT)}
        page={page}
        pageSize={PAGE_SIZE}
        isLoading={isLoading}
        onPageChange={setPage}
        onSearch={setSearch}
        searchValue={search}
        searchPlaceholder="Поиск по имени, username..."
        selectable
        emptyMessage="Нет пользователей, подходящих под условия аудитории"
        onSelectionChange={setSelectedRows}
      />
    </div>
  );
}
